import {
  CommissionSummary_Read,
  InvoiceLineItemType,
  OrderUnitOfMeasure,
  SettlementLineItem_Group_Read,
  SettlementLineItem_Read,
  SettlementPresentation_Read,
} from '@treadinc/horizon-api-spec';
import dayjs, { Dayjs } from 'dayjs';
import { isNil } from 'lodash-es';

import { Nullable } from '~types/Nullable';

class SettlementLineItemQuantity {
  constructor(
    private _quantity: number,
    private _unit_of_measure: OrderUnitOfMeasure,
  ) {}

  public get quantity() {
    return this._quantity;
  }

  public get unitOfMeasure() {
    return this._unit_of_measure;
  }

  public static parse(proto: SettlementLineItem_Read['quantities'][number]) {
    return new SettlementLineItemQuantity(Number(proto.quantity), proto.unit_of_measure);
  }
}

class SettlementLineItemIds {
  constructor(
    private _order_id: string,
    private _job_id: string,
    private _friendly_job_id: string,
    private _friendly_order_id: string,
    private _load_id: Nullable<string>,
    private _friendly_load_id: Nullable<string>,
    private _load_ordinality: Nullable<number>,
  ) {}

  public get orderId() {
    return this._order_id;
  }

  public get jobId() {
    return this._job_id;
  }

  public get friendlyJobId() {
    return this._friendly_job_id;
  }

  public get friendlyOrderId() {
    return this._friendly_order_id;
  }

  public get loadId() {
    return this._load_id;
  }

  public get friendlyLoadId() {
    return this._friendly_load_id;
  }

  public get loadOrdinality() {
    return this._load_ordinality;
  }

  public static parse(proto: SettlementLineItem_Read['ids'][number]) {
    return new SettlementLineItemIds(
      proto.order_id,
      proto.job_id,
      proto.friendly_job_id,
      proto.friendly_order_id,
      proto.load_id,
      proto.friendly_load_id,
      proto.load_ordinality,
    );
  }
}

export class SettlementLineItem {
  constructor(
    private _date: Dayjs,
    private _truck_external_ids: string[],
    private _equipment_types: string[],
    private _equipment_names: string[],
    private _services: string[],
    private _materials: string[],
    private _quantities: SettlementLineItemQuantity[],
    private _ids: SettlementLineItemIds[],
    private _total_quantity: number,
    // Null when the current user lacks the `view_rate` permission and the backend
    // omits the value.
    private _rate_value: Nullable<number>,
    private _rate_types: string[],
    private _total_amount: number,
    private _loads_count: number,
    private _ticket_numbers: Nullable<string[]>,
    private _pay_start_time: Nullable<Dayjs>,
    private _pay_end_time: Nullable<Dayjs>,
    private _billing_adjustment_minutes: Nullable<number>,
    private _rebill_rate_value: Nullable<number>,
    private _rebill_total_amount: Nullable<number>,
    private _vendor_job_assignment: Nullable<VendorJobAssignment>,
    private _driver: Nullable<Driver>,
    private _line_item_type: Nullable<InvoiceLineItemType>,
    private _material_rate_unit_of_measure: Nullable<OrderUnitOfMeasure>,
    private _settlement_line_item_ids: string[] = [],
    private _pay_commission_percentage: Nullable<string> = null,
    private _pay_commission_net_rate: Nullable<string> = null,
    private _net_total_amount: Nullable<number> = null,
    private _rebill_pay_commission_net_rate: Nullable<string> = null,
    private _rebill_net_total_amount: Nullable<number> = null,
  ) {}

  public get payCommissionPercentage() {
    return this._pay_commission_percentage;
  }

  public get payCommissionNetRate() {
    return this._pay_commission_net_rate;
  }

  public get netTotalAmount() {
    return this._net_total_amount;
  }

  public get rebillPayCommissionNetRate() {
    return this._rebill_pay_commission_net_rate;
  }

  public get rebillNetTotalAmount() {
    return this._rebill_net_total_amount;
  }

  public get date() {
    return this._date;
  }

  public get truckExternalIds() {
    return this._truck_external_ids;
  }

  public get equipmentTypes() {
    return this._equipment_types;
  }

  public get equipmentNames() {
    return this._equipment_names;
  }

  public get services() {
    return this._services;
  }

  public get materials() {
    return this._materials;
  }

  public get quantities() {
    return this._quantities;
  }

  public get ids() {
    return this._ids;
  }

  public get totalQuantity() {
    return this._total_quantity;
  }

  public get rateValue() {
    return this._rate_value;
  }

  public get rateTypes() {
    return this._rate_types;
  }

  public get totalAmount() {
    return this._total_amount;
  }

  public get loadsCount() {
    return this._loads_count;
  }

  public get ticketNumbers() {
    return this._ticket_numbers;
  }

  public get payStartTime() {
    return this._pay_start_time;
  }

  public get payEndTime() {
    return this._pay_end_time;
  }

  public get billingAdjustmentMinutes() {
    return this._billing_adjustment_minutes;
  }

  public get rebillRateValue() {
    return this._rebill_rate_value;
  }

  public get rebillTotalAmount() {
    return this._rebill_total_amount;
  }

  public get vendorJobAssignment() {
    return this._vendor_job_assignment;
  }

  public get driver() {
    return this._driver;
  }

  public get lineItemType() {
    return this._line_item_type;
  }

  public get materialRateUnitOfMeasure() {
    return this._material_rate_unit_of_measure;
  }

  public get settlementLineItemIds() {
    return this._settlement_line_item_ids;
  }

  public static parse(proto: SettlementLineItem_Read) {
    return new SettlementLineItem(
      dayjs(proto.date),
      proto.truck_external_ids,
      proto.equipment_types,
      proto.equipment_names,
      proto.services,
      proto.materials,
      proto.quantities.map((quantity) => SettlementLineItemQuantity.parse(quantity)),
      proto.ids.map((id) => SettlementLineItemIds.parse(id)),
      Number(proto.total_quantity),
      isNil(proto.rate_value) ? null : Number(proto.rate_value),
      proto.rate_types,
      Number(proto.total_amount),
      Number(proto.loads_count),
      proto.ticket_numbers ?? null,
      isNil(proto.pay_start_time) ? null : dayjs(proto.pay_start_time),
      isNil(proto.pay_end_time) ? null : dayjs(proto.pay_end_time),
      isNil(proto.billing_adjustment_minutes)
        ? null
        : Number(proto.billing_adjustment_minutes),
      isNil(proto.rebill_rate_value) ? null : Number(proto.rebill_rate_value),
      isNil(proto.rebill_total_amount) ? null : Number(proto.rebill_total_amount),
      proto.vendor_job_assignment
        ? VendorJobAssignment.parse(proto.vendor_job_assignment)
        : null,
      proto.driver ? Driver.parse(proto.driver) : null,
      proto.line_item_type ?? null,
      proto.material_rate_unit_of_measure ?? null,
      proto.settlement_line_item_ids,
      proto.pay_commission_percentage ?? null,
      proto.pay_commission_net_rate ?? null,
      isNil(proto.net_total_amount) ? null : Number(proto.net_total_amount),
      proto.rebill_pay_commission_net_rate ?? null,
      isNil(proto.rebill_net_total_amount) ? null : Number(proto.rebill_net_total_amount),
    );
  }
}

export class SettlementLineItemGroup {
  constructor(
    private _group: Nullable<string>,
    private _line_items: SettlementLineItem[],
    private _subtotal: Nullable<number>,
    private _net_subtotal: Nullable<number> = null,
    private _rebill_net_subtotal: Nullable<number> = null,
  ) {}

  public get netSubtotal() {
    return this._net_subtotal;
  }

  public get rebillNetSubtotal() {
    return this._rebill_net_subtotal;
  }

  public get group() {
    return this._group;
  }

  public get lineItems() {
    return this._line_items;
  }

  public get subtotal() {
    return this._subtotal;
  }

  public static parse(proto: SettlementLineItem_Group_Read) {
    return new SettlementLineItemGroup(
      proto.group ?? null,
      proto.line_items.map((lineItem) => SettlementLineItem.parse(lineItem)),
      isNil(proto.subtotal) ? null : Number(proto.subtotal),
      Number(proto.net_subtotal),
      isNil(proto.rebill_net_subtotal) ? null : Number(proto.rebill_net_subtotal),
    );
  }
}

// TODO: Should be using generated types for driver and vendor once BE API spec has been updated.
class Driver {
  constructor(
    private _id: string,
    private _first_name: string,
    private _last_name: string,
  ) {}

  public get id() {
    return this._id;
  }

  public get firstName() {
    return this._first_name;
  }

  public get lastName() {
    return this._last_name;
  }

  public get fullName() {
    return this._first_name + ' ' + this._last_name;
  }

  public static parse(proto: SettlementLineItem_Read['driver']) {
    if (isNil(proto)) {
      return null;
    }

    return new Driver(proto.id, proto.first_name, proto.last_name);
  }
}

class Vendor {
  constructor(
    private _id: string,
    private _name: string,
  ) {}

  public get id() {
    return this._id;
  }

  public get name() {
    return this._name;
  }
  public static parse(
    proto: NonNullable<
      SettlementLineItem_Read['vendor_job_assignment']
    >['vendor_account'],
  ) {
    if (isNil(proto)) {
      return null;
    }

    return new Vendor(proto.id, proto.name);
  }
}

class VendorJobAssignment {
  constructor(
    private _id: string,
    private _vendor_account: Nullable<Vendor>,
  ) {}

  public get id() {
    return this._id;
  }

  public get vendorAccount() {
    return this._vendor_account;
  }

  public static parse(
    proto: SettlementLineItem_Read['vendor_job_assignment'] | undefined,
  ) {
    if (isNil(proto)) {
      return null;
    }

    return new VendorJobAssignment(proto.id, Vendor.parse(proto.vendor_account));
  }
}

class CommissionSummary {
  constructor(
    private _label: string,
    private _amount: number,
    private _rebill_amount: Nullable<number>,
  ) {}

  public get label() {
    return this._label;
  }

  public get amount() {
    return this._amount;
  }

  public get rebillAmount() {
    return this._rebill_amount;
  }

  public static parse(proto: CommissionSummary_Read) {
    return new CommissionSummary(
      proto.label,
      Number(proto.amount),
      isNil(proto.rebill_amount) ? null : Number(proto.rebill_amount),
    );
  }
}

export class SettlementPresentation {
  constructor(
    private _commission_presentation: SettlementPresentation_Read['commission_presentation'],
    private _commission_summary: Nullable<CommissionSummary>,
  ) {}

  public get commissionPresentation() {
    return this._commission_presentation;
  }

  public get commissionSummary() {
    return this._commission_summary;
  }

  public static parse(proto: SettlementPresentation_Read) {
    return new SettlementPresentation(
      proto.commission_presentation,
      proto.commission_summary ? CommissionSummary.parse(proto.commission_summary) : null,
    );
  }
}
